import { Show } from "solid-js";
import { COMPACT_THRESHOLD, formatCompactNumber } from "@/utils/number";
import { Tooltip, TooltipContent, TooltipTrigger } from "./tooltip";
import { useI18n } from "@/providers/i18n-provider";
import { cn } from "@bso/shared";

interface CounterBadgeProps {
  count: number | undefined;
  class?: string;
  label?: string; // e.g. "tareas", "listas", "archivos"
  tooltipDisabled?: boolean;
  showZero?: boolean;
}

export function CounterBadge(props: CounterBadgeProps) {
  const { locale, t } = useI18n();

  const isCompact = (): boolean => props.count !== undefined && props.count >= COMPACT_THRESHOLD;

  const formattedCount = (): string =>
    props.count === undefined ? "" : formatCompactNumber(props.count, locale());

  // Localized description for accessibility and tooltips
  const fullLabel = (): string => {
    const defaultLabel = props.label ?? "tareas";
    if (props.count === undefined) {
      return "";
    }
    // Handled with standard format or basic translation
    return `${props.count.toLocaleString(locale())} ${t(defaultLabel)}`;
  };

  return (
    <Show
      when={props.count !== undefined && (props.count > 0 || (props.showZero && props.count === 0))}
    >
      <Show
        when={isCompact() && !props.tooltipDisabled}
        fallback={
          <span
            class={cn(
              "inline-flex items-center justify-center px-1.5 py-0.5 rounded-full bg-muted-foreground/10 text-muted-foreground transition-all text-[10px] font-bold select-none animate-fade-in",
              props.class,
            )}
            aria-label={fullLabel()}
          >
            {props.count}
          </span>
        }
      >
        <Tooltip openDelay={350} closeDelay={100}>
          <TooltipTrigger class="focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-accent rounded-full select-none cursor-help">
            <span
              class={cn(
                "inline-flex items-center justify-center px-1.5 py-0.5 rounded-full bg-muted-foreground/10 text-muted-foreground hover:bg-accent/15 hover:text-accent transition-all text-[10px] font-bold animate-fade-in",
                props.class,
              )}
              aria-label={fullLabel()}
            >
              {formattedCount()}
            </span>
          </TooltipTrigger>
          <TooltipContent class="max-w-[200px] text-center font-semibold bg-background text-foreground border border-border/40 shadow-lg px-2.5 py-1.5 rounded-lg select-none">
            {fullLabel()}
          </TooltipContent>
        </Tooltip>
      </Show>
    </Show>
  );
}
